<template>
  <div class="container">
    <div class="body">
      <v-text-field
        autofocus
        filled
        background-color="textBoxBackground"
        :label="userEditObj?.asset_type?.includes('ndi') ? 'Alias' : 'Name'"
        style="height: 66px; padding-right: 12px"
        class="mb-4"
        v-model="userEditObj.name"
        @keyup.enter="$emit('request-save')" />

      <!-- Parent Group Assignment -->
      <v-autocomplete
        filled
        background-color="textBoxBackground"
        label="Parent Group"
        style="height: 66px; padding-right: 12px"
        class="mb-4"
        v-model="selectedGroupUid"
        :items="groupOptions"
        item-title="name"
        item-value="uid"
        clearable
        @update:modelValue="updateParentGroup"
        hint="Assign this asset to a group or leave empty for root level"
        persistent-hint>
        <template v-slot:prepend-inner>
          <v-icon size="small">mdi-folder-multiple</v-icon>
        </template>
      </v-autocomplete>

      <div v-if="userEditObj?.asset_type?.includes('ndi')">
        <b>NDI Source:</b>
        {{ userEditObj.value }}
      </div>

      <!-- Plain value for custom or non-json/text types -->
      <v-text-field
        v-else-if="
          userEditObj.custom &&
          !userEditObj?.asset_type?.includes('ndi') &&
          !userEditObj?.asset_type?.includes('json') &&
          !userEditObj?.asset_type?.includes('text')
        "
        filled
        style="height: 66px"
        label="Value"
        v-model="userEditObj.value"
        @keyup.enter="update_asset_details()" />

      <!-- JSON editor -->
      <div v-else-if="userEditObj?.asset_type?.includes('json')">
        <!-- Live JSON Settings -->
        <div class="live-json-settings mb-4">
          <v-switch
            v-model="liveMode"
            label="Live Mode (fetch from URL)"
            color="primary"
            hide-details
            class="mb-2" />

          <div
            v-if="liveMode"
            class="live-settings-fields">
            <v-text-field
              v-model="liveUrl"
              label="URL"
              placeholder="https://example.com/data.json"
              filled
              background-color="textBoxBackground"
              hide-details
              class="mb-2" />

            <v-text-field
              v-model.number="liveInterval"
              label="Update Interval (seconds)"
              type="number"
              :min="5"
              filled
              background-color="textBoxBackground"
              hide-details
              hint="Minimum 5 seconds" />
          </div>
        </div>

        <JSONEditor
          :item="userEditObj"
          :readOnlyMode="false" />
      </div>

      <!-- Text editor -->
      <div v-else-if="userEditObj?.asset_type?.includes('text')">
        <v-textarea
          autofocus
          dense
          clearable
          filled
          rows="15"
          hide-details
          v-model="userEditObj.value"
          label="Edit text" />
      </div>

      <!-- Preview for media assets -->
      <div
        v-if="
          (userEditObj?.asset_type?.includes('video') ||
            userEditObj?.asset_type?.includes('image') ||
            userEditObj?.asset_type?.includes('live_stream')) &&
          userEditObj?.value
        "
        class="asset-preview-container">
        <AssetPreview
          :selected="userEditObj"
          :height="'calc(100% - 140px)'" />
        <div class="preview-overlay">
          <span
            title="The asset preview is not the original file. It has been compressed, converted to a web-compatible format, and its resolution has been reduced for optimal performance."
            class="preview-label">
            Preview
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="userEditObj?.uid || userEditObj?.asset_uid"
      class="uid-subtle bottom with-tick">
      <small>
        <code
          @click="copyUid"
          class="clickable-uid">
          {{ userEditObj.uid || userEditObj.asset_uid }}
        </code>
      </small>
      <span
        class="copied-tick"
        v-if="copied">
        <v-icon small>mdi-check</v-icon>
      </span>
    </div>
  </div>
</template>

<script setup>
import AssetPreview from './../AssetPreview.vue';
import JSONEditor from '../../Shared/SceneDataSubItems/JSONEditor.vue';
import { onUnmounted, onMounted, ref, watch, computed } from 'vue';
import { $http } from '../../../stores/http';

const props = defineProps(['userEditObj']);
const emit = defineEmits(['request-save']);

// Parent group management
const selectedGroupUid = ref(null);
const groupOptions = ref([]);

// Live JSON settings - sync with userEditObj.info
const liveMode = ref(false);
const liveUrl = ref('');
const liveInterval = ref(60);

// Initialize live settings from userEditObj.info
const initLiveSettings = () => {
  const info = props.userEditObj?.info;
  if (info && typeof info === 'object') {
    liveMode.value = !!info.live;
    liveUrl.value = info.url || '';
    liveInterval.value = info.interval || 60;
  } else {
    liveMode.value = false;
    liveUrl.value = '';
    liveInterval.value = 60;
  }
};

// Watch for changes to live settings and sync back to userEditObj.info
watch([liveMode, liveUrl, liveInterval], () => {
  if (!props.userEditObj) return;

  // Ensure info object exists
  if (!props.userEditObj.info || typeof props.userEditObj.info !== 'object') {
    props.userEditObj.info = {};
  }

  props.userEditObj.info.live = liveMode.value;
  props.userEditObj.info.url = liveUrl.value;
  props.userEditObj.info.interval = Math.max(5, liveInterval.value || 60);
});

// Watch for userEditObj changes (e.g., modal reload)
watch(
  () => props.userEditObj?.info,
  () => {
    initLiveSettings();
  },
  { immediate: true, deep: true }
);

// Load available groups and set current value
const loadGroups = async () => {
  try {
    const response_raw = await $http('GET', '/asset_groups');
    const response = JSON.parse(response_raw);
    groupOptions.value = response.groups || [];

    console.log('[AssetDetails] Loaded groups:', groupOptions.value);
    console.log(
      '[AssetDetails] Asset group UID:',
      props.userEditObj.asset_group_uid
    );

    // Set current value
    selectedGroupUid.value = props.userEditObj.asset_group_uid || null;
  } catch (error) {
    console.error('Failed to load asset groups:', error);
  }
};

// Update parent group assignment
const updateParentGroup = async (newGroupUid) => {
  try {
    if (newGroupUid) {
      // Assign to group
      await $http(
        'PUT',
        '/add_asset_to_group',
        JSON.stringify({
          group_uid: newGroupUid,
          asset_uid: props.userEditObj.uid,
        })
      );
    } else {
      // Remove from group (set to null)
      await $http(
        'PUT',
        '/remove_asset_from_group',
        JSON.stringify({
          asset_uid: props.userEditObj.uid,
        })
      );
    }

    // Update local model
    props.userEditObj.asset_group_uid = newGroupUid;

    // Trigger save/refresh
    emit('request-save');
  } catch (error) {
    console.error('Failed to update parent group:', error);
  }
};

// Watch for changes to userEditObj (modal might load data after mount)
watch(
  () => props.userEditObj?.asset_group_uid,
  (newVal) => {
    console.log('[AssetDetails] asset_group_uid changed to:', newVal);
    selectedGroupUid.value = newVal || null;
  },
  { immediate: true }
);

// Load groups on mount
onMounted(async () => {
  console.log('[AssetDetails] Mounted with asset:', props.userEditObj);
  await loadGroups();
});

// Clean up
onUnmounted(async () => {
  await $http('DELETE', '/cancel_streams');
});
const copied = ref(false);
async function copyUid() {
  const uid = props.userEditObj?.uid || props.userEditObj?.asset_uid || '';
  if (!uid) return;
  try {
    await navigator.clipboard.writeText(uid);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1400);
  } catch (e) {
    const ta = document.createElement('textarea');
    ta.value = uid;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      copied.value = true;
      setTimeout(() => (copied.value = false), 1400);
    } catch (err) {
      console.error('copy failed', err);
    }
    ta.remove();
  }
}
</script>

<style scoped>
.asset-preview-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.preview-overlay {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 4px 8px;
  border-radius: 4px;
  z-index: 1;
}

.preview-label {
  color: white;
  font-size: 14px;
}

.uid-subtle {
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.5);
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono', 'Courier New',
    monospace;
  font-size: 13px;
  word-break: break-all;
}
.uid-subtle code {
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
}
.clickable-uid {
  cursor: pointer;
}

.with-tick {
  display: flex;
  align-items: center;
  gap: 8px;
}
.copied-tick {
  color: rgba(255, 255, 255, 0.8);
}

.live-json-settings {
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

.live-settings-fields {
  margin-top: 8px;
  padding-left: 8px;
}
</style>
